import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, Monitor, Camera, Plus, X, RefreshCw, Copy, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAccount } from '../../context/AccountContext';
import {
  GET_CAPTURE_DEVICES,
  CREATE_CAPTURE_DEVICE,
  REPAIR_CAPTURE_DEVICE,
  REVOKE_CAPTURE_DEVICE,
} from '../../graphql';
import { Loader } from '../common/PageLoader';

/**
 * Capture devices — the things that photograph the collection.
 *
 * Pairing shows a short-lived, single-use code as a QR. The durable token the
 * device ends up holding is never displayed and never round-trips through this
 * screen; the code is exchanged for it directly by the device. That means a QR
 * photographed off someone's shoulder is worthless once it expires or is used.
 */

const KINDS = [
  {
    key: 'browser',
    label: 'Web browser',
    icon: Monitor,
    hint: 'Capture from this or another computer.',
  },
  {
    key: 'mobile',
    label: 'Phone or tablet',
    icon: Smartphone,
    hint: 'Shoot cards in hand, or at a show.',
  },
  {
    key: 'studio',
    label: 'Studio camera',
    icon: Camera,
    hint: 'A fixed rig — USB camera on a stand.',
  },
];

const iconFor = (kind: string) => KINDS.find((k) => k.key === kind)?.icon ?? Monitor;

interface Device {
  id: string;
  name: string;
  kind: string;
  connected: boolean;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  tokenCount: number;
  isActive: boolean;
  createdAt: string;
}

interface Pairing {
  pairingCode: string;
  expiresAt: string;
  deviceName: string;
}

const inputClassName =
  'w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

export function CaptureDevicesSettings() {
  const { accountId, isLoading: accountsLoading } = useAccount();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState('browser');
  const [pairing, setPairing] = useState<Pairing | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, loading, refetch } = useQuery(GET_CAPTURE_DEVICES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  const [createDevice, { loading: creating }] = useMutation(CREATE_CAPTURE_DEVICE);
  const [repairDevice] = useMutation(REPAIR_CAPTURE_DEVICE);
  const [revokeDevice] = useMutation(REVOKE_CAPTURE_DEVICE);

  const devices: Device[] = data?.captureDevices ?? [];

  const handleAdd = async () => {
    if (!accountId) return;
    const { data: created } = await createDevice({ variables: { accountId, name, kind } });
    const r = created?.createCaptureDevice;
    if (r) {
      setPairing({
        pairingCode: r.pairingCode,
        expiresAt: r.expiresAt,
        deviceName: r.device?.name || name || 'New device',
      });
    }
    setAdding(false);
    setName('');
    await refetch();
  };

  const handleRepair = async (d: Device) => {
    if (!accountId) return;
    const { data: r } = await repairDevice({ variables: { accountId, deviceId: d.id } });
    const p = r?.repairCaptureDevice;
    if (p) setPairing({ ...p, deviceName: d.name });
  };

  const handleRevoke = async (d: Device) => {
    if (!accountId) return;
    // Revoking only kills the device's access — the cards it captured are the
    // collection and stay put. Say so, so nobody hesitates over the button.
    if (
      !window.confirm(
        `Disconnect "${d.name}"?\n\nIt will no longer be able to upload. Everything it has already captured stays in your collection.`
      )
    ) {
      return;
    }
    await revokeDevice({ variables: { accountId, deviceId: d.id } });
    await refetch();
  };

  const copyCode = async () => {
    if (!pairing) return;
    await navigator.clipboard.writeText(pairing.pairingCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  if (accountsLoading) return <Loader label="Loading" className="p-6 text-cmp-ink-2" />;

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-cmp-ink flex items-center gap-2">
            <Camera className="w-5 h-5" /> Capture devices
          </h2>
          <p className="text-sm text-cmp-ink-2 mt-1">
            Each device gets its own access, so you can disconnect one without touching the others.
            A device can upload photos and add cards — nothing else.
          </p>
        </div>
        <Button onClick={() => setAdding(true)} className="gap-2 shrink-0">
          <Plus className="w-4 h-4" /> Add device
        </Button>
      </div>

      {adding && (
        <Card className="p-4 space-y-4 bg-cmp-raise border border-cmp-line text-cmp-ink">
          <div>
            <Label htmlFor="deviceName">Name</Label>
            <Input
              id="deviceName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Studio Mac, My iPhone"
              className={inputClassName}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {KINDS.map((k) => {
              const Icon = k.icon;
              return (
                <button
                  key={k.key}
                  onClick={() => setKind(k.key)}
                  className={`text-left p-3 rounded border transition ${
                    kind === k.key
                      ? 'border-cmp-bronze bg-cmp-bronze-soft'
                      : 'border-cmp-line hover:border-cmp-line-2'
                  }`}
                >
                  <Icon className="w-4 h-4 mb-1 text-cmp-ink" />
                  <div className="text-sm text-cmp-ink">{k.label}</div>
                  <div className="text-[11px] text-cmp-ink-2">{k.hint}</div>
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => void handleAdd()} disabled={creating}>
              {creating ? 'Creating…' : 'Create & pair'}
            </Button>
            <Button variant="outline" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {loading && devices.length === 0 && (
        <Loader label="Loading" className="text-sm text-cmp-ink-2" />
      )}

      {!loading && devices.length === 0 && (
        <Card className="p-8 text-center bg-cmp-raise border border-cmp-line">
          <Camera className="w-[2rem] h-[2rem] mx-auto mb-3 opacity-50 text-cmp-ink-2" />
          <p className="text-sm text-cmp-ink-2">
            No devices yet. Add one to start photographing your collection.
          </p>
        </Card>
      )}

      <div className="space-y-2">
        {devices.map((d) => {
          const Icon = iconFor(d.kind);
          return (
            <Card
              key={d.id}
              className="p-3 flex items-center gap-3 bg-cmp-raise border border-cmp-line text-cmp-ink"
            >
              <Icon className="w-5 h-5 text-cmp-ink-2 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-cmp-ink truncate">{d.name}</span>
                  {d.connected ? (
                    <Badge className="text-[10px]">connected</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">
                      not connected
                    </Badge>
                  )}
                </div>
                <div className="text-[11px] text-cmp-ink-2">
                  {d.lastUsedAt
                    ? `Last used ${new Date(d.lastUsedAt).toLocaleString()}`
                    : 'Never used'}
                  {d.lastUsedIp ? ` · ${d.lastUsedIp}` : ''}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() => void handleRepair(d)}
                title="Show a new pairing code"
              >
                <RefreshCw className="w-3 h-3" /> Pair
              </Button>
              {d.connected && (
                <Button size="sm" variant="ghost" onClick={() => void handleRevoke(d)}>
                  Disconnect
                </Button>
              )}
            </Card>
          );
        })}
      </div>

      {pairing && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6">
          <Card className="w-full max-w-md p-6 space-y-4 bg-cmp-raise border border-cmp-line text-cmp-ink shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-cmp-ink">Pair {pairing.deviceName}</h3>
                <p className="text-xs text-cmp-ink-2 mt-1">
                  Scan this on the device, or paste the code into it.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setPairing(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex justify-center bg-white p-4 rounded">
              <QRCodeSVG value={pairing.pairingCode} size={200} level="M" />
            </div>

            <div className="flex items-center gap-2">
              <code className="flex-1 text-[11px] text-cmp-ink-2 break-all bg-cmp-surface rounded px-2 py-1.5">
                {pairing.pairingCode}
              </code>
              <Button size="sm" variant="outline" onClick={() => void copyCode()} className="gap-1">
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>

            <p className="text-[11px] text-cmp-ink-2">
              Expires {new Date(pairing.expiresAt).toLocaleTimeString()}. It works once — after
              that, pair again for a new code. This code is shown only now and cannot be retrieved
              later.
            </p>
          </Card>
        </div>
      )}
    </div>
  );
}
